import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Signup from "./pages/Signup";
import MasterPanel from "./pages/MasterPanel";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BookingEngine from "./pages/BookingEngine";
import DashboardLayout from "./pages/DashboardLayout";
import Overview from "./pages/Overview";
import Reservations from "./pages/Reservations";
import Rooms from "./pages/Rooms";
import RevenueAI from "./pages/RevenueAI";
import CalendarView from "./pages/CalendarView";
import RateShopper from "./pages/RateShopper";
import Reviews from "./pages/Reviews";
import Channels from "./pages/Channels";
import RatePlans from "./pages/RatePlans";
import Housekeeping from "./pages/Housekeeping";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Team from "./pages/Team";
import Guests from "./pages/Guests";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B101E]">
        <div className="text-[#D4AF37] font-display text-xl animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Master admin shouldn't sit in the property dashboard — push them to /master
  if (user.role === "master_admin") return <Navigate to="/master" replace />;
  return children;
}

function MasterRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B101E]">
        <div className="text-[#D4AF37] font-display text-xl animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "master_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/master" element={<MasterRoute><MasterPanel /></MasterRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/book/:hotelId" element={<BookingEngine />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Overview />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="reservations" element={<Reservations />} />
            <Route path="rooms" element={<Rooms />} />
            <Route path="channels" element={<Channels />} />
            <Route path="rate-plans" element={<RatePlans />} />
            <Route path="rate-shopper" element={<RateShopper />} />
            <Route path="housekeeping" element={<Housekeeping />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="revenue-ai" element={<RevenueAI />} />
            <Route path="reports" element={<Reports />} />
            <Route path="guests" element={<Guests />} />
            <Route path="team" element={<Team />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}
